/* eslint-disable max-lines-per-function */
import React from "react";
import { Stack, Tab, Tabs, Typography } from "@mui/material";
import { AddCircleOutline } from "@mui/icons-material";
import { Button } from "components/haven-design/buttons";
import { BuilderQuestion } from "./form-builder-utils";
import {
  CHILD_LINKED_QUESTIONS_TAB,
  FormQuestionVariant,
  QuestionGroupsBySection,
  UNASSIGNED_QUESTIONS_TAB,
} from "./form-builder-shared";
import FormBuilderQuestionCard from "./form-builder-question-card";

type Props = {
  selectedQuestionTab: string;
  onSelectQuestionTab: (tabId: string) => void;
  questionGroupsBySection: QuestionGroupsBySection;
  questionsInActiveTab: BuilderQuestion[];
  expandedQuestionIds: Set<string>;
  onExpandVisibleQuestions: () => void;
  onCollapseVisibleQuestions: () => void;
  onToggleQuestionExpanded: (questionId: string) => void;
  onDeleteQuestion: (questionId: string, questionKey: string) => void;
  onAddQuestion: () => void;
  onUpdateQuestion: (
    questionId: string,
    update: (question: BuilderQuestion) => BuilderQuestion,
  ) => void;
  onApplyVariant: (
    questionId: string,
    variant: FormQuestionVariant | null,
    preserveIdentity?: boolean,
  ) => void;
  variants: FormQuestionVariant[];
  existingQuestions: Record<string, string>;
  questionIdentityOptions: string[];
  contextSuggestions: string[];
  questionIdentityLabelMap: Record<string, string>;
  questionIdentityTypeMap: Record<string, string>;
};

const FormBuilderQuestionsPanel: React.FC<Props> = ({
  selectedQuestionTab,
  onSelectQuestionTab,
  questionGroupsBySection,
  questionsInActiveTab,
  expandedQuestionIds,
  onExpandVisibleQuestions,
  onCollapseVisibleQuestions,
  onToggleQuestionExpanded,
  onDeleteQuestion,
  onAddQuestion,
  onUpdateQuestion,
  onApplyVariant,
  variants,
  existingQuestions,
  questionIdentityOptions,
  contextSuggestions,
  questionIdentityLabelMap,
  questionIdentityTypeMap,
}) => {
  const unassignedQuestionIds = new Set(
    questionGroupsBySection.unassignedQuestions.map((question) => question.draftId),
  );
  const hasExpandedVisibleQuestions = questionsInActiveTab
    .some((question) => expandedQuestionIds.has(question.draftId));
  const areAllVisibleQuestionsExpanded = questionsInActiveTab.length > 0 && questionsInActiveTab
    .every((question) => expandedQuestionIds.has(question.draftId));

  return (
    <>
      <Stack
        spacing={1}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          backgroundColor: "background.paper",
          pb: 1,
          mb: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Questions</Typography>
          <Button
            size="S"
            type="button"
            color="secondary"
            onClick={onExpandVisibleQuestions}
            disabled={areAllVisibleQuestionsExpanded}
          >
            Expand Tab
          </Button>
          <Button
            size="S"
            type="button"
            color="secondary"
            onClick={onCollapseVisibleQuestions}
            disabled={!hasExpandedVisibleQuestions}
          >
            Collapse Tab
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Only unassigned questions can edit name and context.
        </Typography>
        <Tabs
          value={selectedQuestionTab}
          onChange={(_, value) => onSelectQuestionTab(String(value))}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab
            value={UNASSIGNED_QUESTIONS_TAB}
            label={`Unassigned (${questionGroupsBySection.unassignedQuestions.length})`}
          />
          <Tab
            value={CHILD_LINKED_QUESTIONS_TAB}
            label={`Child Linked (${questionGroupsBySection.childLinkedQuestions.length})`}
          />
          {questionGroupsBySection.sectionGroups.map(({ section, questions }) => (
            <Tab
              key={section.draftId}
              value={section.draftId}
              label={`${section.title || section.name || "(untitled section)"} (${questions.length})`}
            />
          ))}
        </Tabs>
      </Stack>
      <Stack spacing={2}>
        {questionsInActiveTab.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No questions in this tab.
          </Typography>
        )}
        {questionsInActiveTab.map((question) => (
          <FormBuilderQuestionCard
            key={question.draftId}
            question={question}
            existingQuestions={existingQuestions}
            questionIdentityOptions={questionIdentityOptions}
            contextSuggestions={contextSuggestions}
            questionIdentityLabelMap={questionIdentityLabelMap}
            questionIdentityTypeMap={questionIdentityTypeMap}
            questionGroupsBySection={questionGroupsBySection}
            variants={variants}
            canEditIdentity={unassignedQuestionIds.has(question.draftId)}
            isExpanded={expandedQuestionIds.has(question.draftId)}
            onToggleExpanded={onToggleQuestionExpanded}
            onDeleteQuestion={onDeleteQuestion}
            onUpdateQuestion={onUpdateQuestion}
            onApplyVariant={onApplyVariant}
          />
        ))}
        <Button
          size="L"
          type="button"
          color="secondary"
          onClick={onAddQuestion}
        >
          <AddCircleOutline fontSize="small" style={{ marginRight: "0.3rem" }} />
          Add Question
        </Button>
      </Stack>
      {questionsInActiveTab.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: (theme) => theme.zIndex.modal - 1,
            backgroundColor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            boxShadow: 3,
            p: 0.5,
          }}
        >
          <Button
            size="S"
            type="button"
            color="secondary"
            onClick={onCollapseVisibleQuestions}
            disabled={!hasExpandedVisibleQuestions}
          >
            Collapse Tab
          </Button>
          <Button
            size="S"
            type="button"
            color="secondary"
            onClick={onExpandVisibleQuestions}
            disabled={areAllVisibleQuestionsExpanded}
          >
            Expand Tab
          </Button>
        </Stack>
      )}
    </>
  );
};

export default FormBuilderQuestionsPanel;
